import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSite } from "../../lib/useSite";
import { Eyebrow } from "../../components/bits";
import { ArrowRight, Sparkles, CalendarCheck, Repeat } from "lucide-react";

const TIER_BLURB = {
  "Essential": "Everyday cleaning to a high standard — £25/hour, min 3 hours.",
  "Premium": "Enhanced attention to detail with priority scheduling — £35/hour, min 3 hours.",
  "Elite Signature": "Our signature white-glove clean — £50/hour, min 4 hours.",
};

export default function Book() {
  const site = useSite();
  const { user } = useAuth();
  const nav = useNavigate();
  const tiers = (site?.packages || []).filter((p) => p.type === "hourly");

  const start = (tier) => {
    if (user && (user.role === "customer" || user.role === "founder_ceo")) nav(`/portal?book=${encodeURIComponent(tier)}`);
    else nav(`/login?next=${encodeURIComponent(`/portal?book=${tier}`)}`);
  };

  return (
    <div>
      <section className="bg-[#0a0a0a] relative">
        <div className="absolute inset-0 noise-overlay" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 pt-16 pb-12">
          <Eyebrow dark>Book a Clean</Eyebrow>
          <h1 className="font-display text-[#fdfbf7] text-4xl sm:text-5xl tracking-tight">Book online in minutes</h1>
          <p className="text-[#cfcabf] mt-4">Choose your clean, pick a date and time with live availability, add any specialist services, and pay securely. We verify a qualified cleaner before confirming — no empty promises.</p>
          {!user && <p className="text-[#a3a3a3] text-sm mt-3">You'll sign in or create a free account to manage properties, payments and history.</p>}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {tiers.map((p) => (
            <div key={p.name} className={`p-6 rounded-sm border flex flex-col ${p.name === "Elite Signature" ? "bg-[#0a0a0a] border-[#d4af37]" : "bg-white border-[#e8e5de]"}`} data-testid={`book-tier-${p.name.replace(/ /g, "-")}`}>
              <p className={`text-[11px] tracking-luxe uppercase ${p.name === "Elite Signature" ? "text-[#d4af37]" : "text-[#b5952f]"}`}>{p.name}</p>
              <p className={`font-display text-4xl mt-3 ${p.name === "Elite Signature" ? "text-[#fdfbf7]" : "text-[#0a0a0a]"}`}>£{p.rate}<span className="text-sm text-[#8a8a8a]">/hr</span></p>
              <p className={`text-sm mt-3 flex-1 ${p.name === "Elite Signature" ? "text-[#cfcabf]" : "text-[#555]"}`}>{TIER_BLURB[p.name] || p.description}</p>
              <button onClick={() => start(p.name)} data-testid={`book-start-${p.name.replace(/ /g, "-")}`} className={`mt-5 py-3 rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2 ${p.name === "Elite Signature" ? "btn-gold" : "btn-outline-gold"}`}>
                <CalendarCheck size={15} /> Start booking
              </button>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          <Link to="/specialist" className="card-luxe p-5 flex items-center justify-between hover:border-[#d4af37]" data-testid="book-link-specialist">
            <span className="flex items-center gap-2 text-sm"><Sparkles size={16} className="text-[#d4af37]" /> Specialist services</span><ArrowRight size={16} />
          </Link>
          <Link to="/pricing#memberships" className="card-luxe p-5 flex items-center justify-between hover:border-[#d4af37]" data-testid="book-link-memberships">
            <span className="flex items-center gap-2 text-sm"><Repeat size={16} className="text-[#d4af37]" /> Memberships</span><ArrowRight size={16} />
          </Link>
          <Link to="/quote" className="card-luxe p-5 flex items-center justify-between hover:border-[#d4af37]" data-testid="book-link-bespoke">
            <span className="text-sm">Bespoke / Diamond Elite Ultra</span><ArrowRight size={16} />
          </Link>
        </div>
        <p className="text-xs text-[#8a8a8a] mt-6 text-center">Commercial, end-of-tenancy and other assessed services are arranged via <Link to="/quote" className="text-[#b5952f] underline">Request a Quote</Link>.</p>
      </section>
    </div>
  );
}
